"use client";

/**
 * Categorical bar chart の密度パラメータをまとめた値オブジェクト。
 *
 * `BarChart` と `ComposedChart` の双方で同じルールを再利用し、
 * 系列数やカテゴリ件数が変わっても見た目を揃えやすくする。
 */
export type CategoricalBarSizing = {
  barSize: number;
  barCategoryGap: number;
  barGap: number;
};

type ResolveBarComparisonSizingArgs = {
  chartHeight: number;
  rowCount: number;
  isVertical: boolean;
  grouped: boolean;
  seriesCount: number;
  verticalPadding: number;
};

type ResolveBarRankingSizingArgs = {
  chartHeight: number;
  rowCount: number;
  isVertical: boolean;
  verticalPadding: number;
};

type ResolveBarSeparatorStyleArgs = {
  stacking: "grouped" | "stacked" | null | undefined;
};

/**
 * bar segment の境界線見え方を揃えるための stroke 設定。
 *
 * stacked 時だけ区切り線を少し弱めて、series 識別と塗り面積の読み取りを両立する。
 */
export type BarSeparatorStyle = {
  strokeOpacity: number;
  strokeWidth: number;
};

const MODERN_BAR_MIN_SIZE = 8;
const LOW_DENSITY_CATEGORY_GAP = 14;
const MEDIUM_DENSITY_CATEGORY_GAP = 10;
const HIGH_DENSITY_CATEGORY_GAP = 8;
const VERY_HIGH_DENSITY_CATEGORY_GAP = 6;
const LOW_DENSITY_GROUP_GAP = 4;
const HIGH_DENSITY_GROUP_GAP = 2;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function resolveCategoryGap(rowCount: number): number {
  if (rowCount >= 24) return VERY_HIGH_DENSITY_CATEGORY_GAP;
  if (rowCount >= 16) return HIGH_DENSITY_CATEGORY_GAP;
  if (rowCount >= 8) return MEDIUM_DENSITY_CATEGORY_GAP;
  return LOW_DENSITY_CATEGORY_GAP;
}

function resolveGroupGap(rowCount: number): number {
  return rowCount >= 16 ? HIGH_DENSITY_GROUP_GAP : LOW_DENSITY_GROUP_GAP;
}

/**
 * `bar-comparison` の棒の太さと間隔を、件数密度に応じてモダン寄りへ調整する。
 *
 * @remarks
 * 少数カテゴリ時の「太すぎる bar」を抑えつつ、多数カテゴリ時は情報密度を保つため
 * rowCount と orientation ごとに上限値だけを段階的に変える。
 */
export function resolveBarComparisonSizing({
  chartHeight,
  rowCount,
  isVertical,
  grouped,
  seriesCount,
  verticalPadding,
}: ResolveBarComparisonSizingArgs): CategoricalBarSizing {
  const barCategoryGap = resolveCategoryGap(rowCount);
  const barGap = grouped ? resolveGroupGap(rowCount) : 0;

  if (isVertical) {
    const slotSize = Math.max(
      1,
      Math.floor((chartHeight - verticalPadding) / Math.max(1, rowCount)),
    );

    if (grouped) {
      const available = Math.max(1, slotSize - 10);
      const perSeriesSlot = Math.floor(
        (available - (seriesCount - 1) * barGap) / Math.max(1, seriesCount),
      );
      return {
        barSize: clamp(perSeriesSlot, MODERN_BAR_MIN_SIZE, 18),
        barCategoryGap,
        barGap,
      };
    }

    return {
      barSize: clamp(slotSize - 8, MODERN_BAR_MIN_SIZE, 20),
      barCategoryGap,
      barGap,
    };
  }

  const computedBarSize =
    rowCount >= 24 ? 12 : rowCount >= 16 ? 16 : rowCount >= 8 ? 18 : 20;
  const minSize = grouped
    ? Math.max(
        MODERN_BAR_MIN_SIZE,
        Math.floor(computedBarSize / Math.min(2, Math.max(1, seriesCount))),
      )
    : MODERN_BAR_MIN_SIZE;

  return {
    barSize: clamp(computedBarSize, minSize, 20),
    barCategoryGap,
    barGap,
  };
}

/**
 * `bar-ranking` の bar density を、単一系列前提の読みやすい太さへ調整する。
 */
export function resolveBarRankingSizing({
  chartHeight,
  rowCount,
  isVertical,
  verticalPadding,
}: ResolveBarRankingSizingArgs): CategoricalBarSizing {
  const barCategoryGap = resolveCategoryGap(rowCount);

  if (isVertical) {
    const slotHeight = Math.max(
      1,
      (chartHeight - verticalPadding) / Math.max(1, rowCount),
    );
    const maxBarSize = Math.max(
      MODERN_BAR_MIN_SIZE,
      Math.floor(Math.min(32, slotHeight - 10)),
    );
    return {
      barSize: Math.min(20, maxBarSize),
      barCategoryGap,
      barGap: 0,
    };
  }

  return {
    barSize:
      rowCount >= 24 ? 12 : rowCount >= 16 ? 16 : rowCount >= 8 ? 18 : 20,
    barCategoryGap,
    barGap: 0,
  };
}

/**
 * grouped / stacked の違いに応じて bar の境界線強度を解決する。
 *
 * @remarks
 * stacked は色だけに頼ると segment 境界が埋もれやすいため、subtle な stroke を残す。
 * grouped は series 自体が独立しているので、従来どおりやや強めの separator を維持する。
 */
export function resolveBarSeparatorStyle({
  stacking,
}: ResolveBarSeparatorStyleArgs): BarSeparatorStyle {
  if (stacking === "stacked") {
    return {
      strokeOpacity: 0.5,
      strokeWidth: 1,
    };
  }

  return {
    strokeOpacity: 0.9,
    strokeWidth: 1,
  };
}

/**
 * `bar-comparison` の legend を表示すべきかを判定する。
 *
 * 単一系列では title / axis / tooltip と情報が重複しやすいため、2 系列以上だけ表示する。
 */
export function shouldShowBarComparisonLegend(seriesCount: number): boolean {
  return seriesCount > 1;
}
